import { Alert, Button, Card, Form, Modal, Select, Skeleton, Space, Switch, Table, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { useRebuildableJobsQuery } from '../RebuildSnapshots/rebuildSnapshotsQueries'
import { filterJobOption, toJobOptions } from '../RebuildSnapshots/rebuildSnapshotsModel'
import {
  useDiscrepancyJobsQuery,
  useMarkUnbillableMutation,
  useMatchBilledPriceMutation,
  useResolveInvoicedLinesMutation,
  useUnbilledWorkQuery,
} from './markUnbillableQueries'
import { InvoicedLineResolution, UnbilledWorkarea } from './markUnbillableApi'
import {
  InvoicedLineChoices,
  UnbilledLineRow,
  choiceSummaryLines,
  confirmTitle,
  getLoadErrorMessage,
  getMarkErrorMessage,
  getMarkedMessage,
  getMatchErrorMessage,
  getMatchedMessage,
  getResolveErrorMessage,
  getResolvedMessage,
  getResultSummary,
  markButtonLabel,
  matchConfirmTitle,
  matchLineDescriptions,
  matchSummarySentence,
  matchableLineIdsOn,
  noDiscrepancyJobsMessage,
  resolveConfirmTitle,
  selectedTotal,
  selectedWorkareaIdsOn,
  summarizeChoices,
  summarizeMatch,
  toDiscrepancyJobOptions,
  toInvoicedLineChoices,
  toMatchBilledPriceRequest,
  toLineRows,
  toMarkUnbillableRequest,
  toResolveInvoicedLinesRequest,
  toUnbilledWorkRequest,
} from './markUnbillableModel'
import {
  formatCents,
  getJobNotFoundMessage,
  isJobNotFoundError,
  workareaPricingLabel,
  workareaTitle,
} from '../adminJobLookupModel'
import RepricedLinesSection from './RepricedLinesSection'
import UnchargedLinesSection from './UnchargedLinesSection'

const { Paragraph, Text } = Typography

const workareaColumns = [
  {
    title: 'Work area',
    key: 'workarea',
    render: (_: unknown, workarea: UnbilledWorkarea) => <Text strong>{workareaTitle(workarea)}</Text>,
  },
  {
    title: 'Pricing',
    key: 'pricing',
    width: 170,
    render: (_: unknown, workarea: UnbilledWorkarea) => <Tag>{workareaPricingLabel(workarea)}</Tag>,
  },
  {
    title: 'Total',
    key: 'total',
    width: 130,
    align: 'right' as const,
    render: (_: unknown, workarea: UnbilledWorkarea) => formatCents(workarea.total),
  },
]

const lineColumns = [
  { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 70 },
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 120 },
  { title: 'Visit date', dataIndex: 'visitDate', key: 'visitDate', width: 110 },
  { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 110, align: 'right' as const },
]

type WriteMutation = {
  isLoading: boolean
  reset: () => void
}

const WorkareaLines = ({ workarea }: { workarea: UnbilledWorkarea }) => (
  <Table<UnbilledLineRow>
    size="small"
    columns={lineColumns}
    dataSource={toLineRows(workarea)}
    pagination={false}
  />
)

const MarkUnbillableSection = () => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [tenantId, setTenantId] = useState<number | undefined>(undefined)
  const [jobId, setJobId] = useState<number | undefined>(undefined)
  const [selectedWorkareaIds, setSelectedWorkareaIds] = useState<number[]>([])
  const [lineChoices, setLineChoices] = useState<InvoicedLineChoices>({})
  const [selectedRepricedIds, setSelectedRepricedIds] = useState<string[]>([])
  const [showAllJobs, setShowAllJobs] = useState(false)
  const tenantsQuery = useSidebarTenantsQuery()
  const discrepancyJobsQuery = useDiscrepancyJobsQuery(tenantId, !showAllJobs)
  const allJobsQuery = useRebuildableJobsQuery(tenantId, showAllJobs)
  const jobsQuery = showAllJobs ? allJobsQuery : discrepancyJobsQuery
  const jobOptions = showAllJobs ? toJobOptions(allJobsQuery.data ?? []) : toDiscrepancyJobOptions(discrepancyJobsQuery.data ?? [])
  const request = toUnbilledWorkRequest({ tenantId, jobId })
  const unbilledQuery = useUnbilledWorkQuery(request)
  const mutation = useMarkUnbillableMutation()
  const resolveMutation = useResolveInvoicedLinesMutation()
  const matchMutation = useMatchBilledPriceMutation()

  const workareas = unbilledQuery.data?.workareas ?? []
  const selectedIds = selectedWorkareaIdsOn(workareas, selectedWorkareaIds)
  const markRequest = toMarkUnbillableRequest(request, selectedIds)
  const unchargedLines = unbilledQuery.data?.unchargedLines ?? []
  const repricedLines = unbilledQuery.data?.repricedLines ?? []
  const matchLineIds = matchableLineIdsOn(repricedLines, selectedRepricedIds)
  const matchRequest = toMatchBilledPriceRequest(request, matchLineIds)
  const writes: WriteMutation[] = [mutation, resolveMutation, matchMutation]
  const writing = writes.some(write => write.isLoading)
  const othersLoading = (active: WriteMutation) => writes.some(write => write !== active && write.isLoading)
  const resetOthers = (active: WriteMutation) => writes.filter(write => write !== active).forEach(write => write.reset())
  const chosenLines = toInvoicedLineChoices(unchargedLines, lineChoices)
  const resolveRequest = toResolveInvoicedLinesRequest(request, chosenLines)

  const resetActions = () => {
    setSelectedWorkareaIds([])
    setLineChoices({})
    setSelectedRepricedIds([])
    writes.forEach(write => write.reset())
  }

  const handleMark = () => {
    if (markRequest === null) {
      return
    }
    modal.confirm({
      title: confirmTitle(markRequest.workareaIds.length),
      okText: 'Mark unbillable',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            Selected total: <Text strong>{formatCents(selectedTotal(workareas, markRequest.workareaIds))}</Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Every uninvoiced billable item and labor line in the selected work areas will be set to
            unbillable and will no longer show on the tenant&apos;s Jobs to Invoice screen.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        resetOthers(mutation)
        mutation.mutate(markRequest, {
          onSuccess: () => setSelectedWorkareaIds([]),
        })
      },
    })
  }

  const handleLineChoice = (lineId: string, resolution: InvoicedLineResolution) => {
    setLineChoices(current => ({ ...current, [lineId]: resolution }))
  }

  const handleResolve = () => {
    if (resolveRequest === null) {
      return
    }
    modal.confirm({
      title: resolveConfirmTitle(resolveRequest.lines.length),
      okText: 'Resolve lines',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      width: 540,
      content: (
        <div>
          {choiceSummaryLines(summarizeChoices(unchargedLines, resolveRequest.lines)).map(line => (
            <Paragraph key={line} strong style={{ marginBottom: 8 }}>{line}</Paragraph>
          ))}
          <Paragraph style={{ marginBottom: 0 }}>
            Written-off lines are set to unbillable. Lines to bill are released from their invoice and
            will show as unbilled work on the tenant&apos;s Jobs to Invoice screen.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        resetOthers(resolveMutation)
        resolveMutation.mutate(resolveRequest, {
          onSuccess: () => setLineChoices({}),
        })
      },
    })
  }

  const handleMatch = () => {
    if (matchRequest === null) {
      return
    }
    modal.confirm({
      title: matchConfirmTitle(matchRequest.lineIds.length),
      okText: 'Match billed price',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      width: 540,
      content: (
        <div>
          {matchLineDescriptions(repricedLines, matchRequest.lineIds).map(line => (
            <Paragraph key={line.lineId} style={{ marginBottom: 8 }}>{line.text}</Paragraph>
          ))}
          <Paragraph strong style={{ marginBottom: 8 }}>
            {matchSummarySentence(summarizeMatch(repricedLines, matchRequest.lineIds))}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Each line&apos;s price is set to what was billed. Timesheets and hours, and so payroll, stay the same.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        resetOthers(matchMutation)
        matchMutation.mutate(matchRequest, {
          onSuccess: () => setSelectedRepricedIds([]),
        })
      },
    })
  }

  return (
    <Card title="Mark Unbillable" style={{ maxWidth: 1200 }}>
      {modalContextHolder}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          The job list shows only jobs with invoiced lines that were never charged or were repriced,
          largest first; turn on Show all jobs to pick any job. Writes off a job&apos;s leftover unbilled
          time-and-materials or flat-rate work by marking the selected work areas&apos; uninvoiced items and
          labor unbillable, resolves lines that were marked invoiced but never charged, and matches
          repriced labor lines to what was billed.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Tenant">
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={tenantId}
              onChange={(value: number) => {
                setTenantId(value)
                setJobId(undefined)
                resetActions()
              }}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Switch
                aria-label="Show all jobs"
                checked={showAllJobs}
                onChange={checked => {
                  setShowAllJobs(checked)
                  setJobId(undefined)
                  resetActions()
                }}
              />
              <Text>Show all jobs</Text>
            </Space>
          </Form.Item>
          <Form.Item label="Job">
            <Select
              showSearch
              placeholder={tenantId === undefined ? 'Select a tenant first' : 'Search for a job'}
              value={jobId}
              onChange={(value: number) => {
                setJobId(value)
                resetActions()
              }}
              options={jobOptions}
              filterOption={filterJobOption}
              loading={jobsQuery.isLoading}
              disabled={tenantId === undefined}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        {!showAllJobs && discrepancyJobsQuery.isSuccess && discrepancyJobsQuery.data.length === 0 && (
          <Alert type="info" showIcon message={noDiscrepancyJobsMessage} />
        )}

        {jobsQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the tenant's jobs"
            description="Change the tenant or re-select it to try again."
          />
        )}

        {unbilledQuery.isLoading && <Skeleton active />}

        {mutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message={getMarkedMessage(mutation.variables?.workareaIds.length ?? 0)}
          />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Write-off failed"
            description={getMarkErrorMessage(mutation.error)}
          />
        )}

        {resolveMutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message={getResolvedMessage(resolveMutation.variables?.lines ?? [])}
          />
        )}

        {resolveMutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Resolving invoiced lines failed"
            description={getResolveErrorMessage(resolveMutation.error)}
          />
        )}

        {matchMutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message={getMatchedMessage(matchMutation.variables?.lineIds.length ?? 0)}
          />
        )}

        {matchMutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Matching the billed price failed"
            description={getMatchErrorMessage(matchMutation.error)}
          />
        )}

        {unbilledQuery.isSuccess && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert type="info" showIcon message={getResultSummary(unbilledQuery.data)} />
            {workareas.length > 0 && (
              <>
                <Table<UnbilledWorkarea>
                  rowKey="workareaId"
                  columns={workareaColumns}
                  dataSource={workareas}
                  pagination={false}
                  loading={unbilledQuery.isFetching}
                  rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: keys => setSelectedWorkareaIds(keys as number[]),
                    getCheckboxProps: () => ({ disabled: writing }),
                  }}
                  expandable={{
                    expandedRowRender: workarea => <WorkareaLines workarea={workarea} />,
                    expandedRowKeys: workareas.map(workarea => workarea.workareaId),
                    showExpandColumn: false,
                  }}
                />
                <Space>
                  <Button
                    type="primary"
                    danger
                    onClick={handleMark}
                    loading={mutation.isLoading}
                    disabled={markRequest === null || othersLoading(mutation)}
                  >
                    {markButtonLabel(selectedIds.length)}
                  </Button>
                  <Text type="secondary">
                    Selected total: {formatCents(selectedTotal(workareas, selectedIds))}
                  </Text>
                </Space>
              </>
            )}
            <UnchargedLinesSection
              lines={unchargedLines}
              choices={lineChoices}
              choiceCount={chosenLines.length}
              loading={unbilledQuery.isFetching}
              submitting={resolveMutation.isLoading}
              disabled={othersLoading(resolveMutation)}
              onChoiceChange={handleLineChoice}
              onClearChoices={() => setLineChoices({})}
              onSubmit={handleResolve}
            />
            <RepricedLinesSection
              lines={repricedLines}
              selectedLineIds={matchLineIds}
              loading={unbilledQuery.isFetching}
              submitting={matchMutation.isLoading}
              disabled={othersLoading(matchMutation)}
              onSelectionChange={setSelectedRepricedIds}
              onSubmit={handleMatch}
            />
          </Space>
        )}

        {unbilledQuery.isError && isJobNotFoundError(unbilledQuery.error) && (
          <Alert
            type="warning"
            showIcon
            message="Job not found"
            description={getJobNotFoundMessage(unbilledQuery.error, request)}
          />
        )}

        {unbilledQuery.isError && !isJobNotFoundError(unbilledQuery.error) && (
          <Alert
            type="error"
            showIcon
            message="Load failed"
            description={getLoadErrorMessage(unbilledQuery.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default MarkUnbillableSection
